import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { AppShell, type ModuleId } from './AppShell';
import { SplitCrestIntro } from './SplitCrestIntro';
import { ModulePlaceholder } from './ModulePlaceholder';
import { ConnectionScreen } from './ConnectionScreen';
import { LoginScreen } from './LoginScreen';
import { Dashboard } from './Dashboard';
import { Players } from './Players';
import { MatchCentre } from './MatchCentre';
import { MatchdayCommandCentre } from './MatchdayCommandCentre';
import { PostMatchReview } from './PostMatchReview';
import { TrainingCentre } from './TrainingCentre';
import { CoachIntelligence } from './CoachIntelligence';
import { LineupBuilder } from './LineupBuilder';
import { HarmonyNetwork } from './HarmonyNetwork';
import { LeagueCentre } from './LeagueCentre';
import { SettingsCentre } from './SettingsCentre';
import { NewsCentre } from './NewsCentre';
import { OperationsCentre } from './OperationsCentre';
import { DataProvider, useData } from './DataContext';
import { getStoredPublicKey, getSupabase } from './supabase';
import { useI18n } from './I18nContext';
import { LoadingCrest } from './LoadingCrest';
import { ModuleErrorBoundary } from './ModuleErrorBoundary';
import { PlayerSquadView } from './PlayerSquadView';
import { PlayerMatchdayView } from './PlayerMatchdayView';
import { PlayerNewsView } from './PlayerNewsView';
import { isPlayerRole, isStaffRole, moduleAllowedForRole } from './staffAccess';

function V23Router({ session, onLogout }: { session: Session; onLogout: () => Promise<void> }) {
  const { t } = useI18n();
  const { team } = useData();
  const [module, setModule] = useState<ModuleId>('dashboard');
  const [requestedPlayerId, setRequestedPlayerId] = useState<string | null>(null);

  useEffect(() => {
    if (!moduleAllowedForRole(module, team?.role)) setModule('dashboard');
  }, [module, team?.role]);

  const safeNavigate = (next: ModuleId) => {
    setModule(moduleAllowedForRole(next, team?.role) ? next : 'dashboard');
  };

  const titles: Record<ModuleId, string> = {
    dashboard: t.dashboard, operations: 'Operations', players: t.players, matches: t.matches, matchday: t.matchday,
    analysis: t.analysis, training: t.training, intelligence: t.intelligence, lineup: t.lineup,
    harmony: t.harmony, league: t.league, news: t.news, settings: t.settings,
  };

  return (
    <AppShell active={module} onNavigate={safeNavigate} onLogout={() => void onLogout()}>
      <ModuleErrorBoundary moduleName={titles[module]}>
        {module === 'dashboard' && <Dashboard onNavigate={safeNavigate} />}
        {module === 'operations' && <OperationsCentre onNavigate={safeNavigate} />}
        {module === 'players' && ((isPlayerRole(team?.role) || isStaffRole(team?.role)) ? <PlayerSquadView /> : <Players requestedPlayerId={requestedPlayerId} onRequestedPlayerHandled={() => setRequestedPlayerId(null)} />)}
        {module === 'matches' && <MatchCentre />}
        {module === 'matchday' && (isPlayerRole(team?.role) ? <PlayerMatchdayView /> : <MatchdayCommandCentre onNavigate={safeNavigate} />)}
        {module === 'analysis' && <PostMatchReview />}
        {module === 'training' && <TrainingCentre />}
        {module === 'intelligence' && <CoachIntelligence />}
        {module === 'lineup' && <LineupBuilder />}
        {module === 'harmony' && <HarmonyNetwork />}
        {module === 'news' && (isPlayerRole(team?.role) ? <PlayerNewsView /> : <NewsCentre />)}
        {module === 'league' && <LeagueCentre onNavigate={safeNavigate} onOpenPlayer={(playerId) => { setRequestedPlayerId(playerId); safeNavigate('players'); }} />}
        {module === 'settings' && <SettingsCentre />}
        {!['dashboard','operations','players','matches','matchday','analysis','training','intelligence','lineup','harmony','league','news','settings'].includes(module) && <ModulePlaceholder title={titles[module]} />}
      </ModuleErrorBoundary>
    </AppShell>
  );
}

export default function App() {
  const [hasKey, setHasKey] = useState(Boolean(getStoredPublicKey()));
  const [session, setSession] = useState<Session | null>(null);
  const [checking, setChecking] = useState(true);
  const [introTrigger, setIntroTrigger] = useState(() => Date.now());

  useEffect(() => {
    // V23.2.5: app-wide numeric input hardening. iOS/Safari may keep the
    // rendered zero in a controlled <input type=number>, causing 30 to
    // appear as 030. Clear a zero-only field in the DOM on focus so the
    // first keystroke always replaces it. On blur, normalise any remaining
    // leading-zero representation without changing legitimate decimals.
    const isNumericField = (target: EventTarget | null): target is HTMLInputElement => {
      if (!(target instanceof HTMLInputElement)) return false;
      if (target.type === 'range') return false;
      return target.type === 'number' || target.inputMode === 'numeric' || target.inputMode === 'decimal';
    };

    const handleNumericFocus = (event: FocusEvent) => {
      if (!isNumericField(event.target)) return;
      const input = event.target;
      if (/^[+-]?0(?:\.0+)?$/.test(input.value.trim())) {
        input.value = '';
      }
    };

    const handleNumericBlur = (event: FocusEvent) => {
      if (!isNumericField(event.target)) return;
      const input = event.target;
      const raw = input.value.trim();
      if (!raw) {
        input.value = '0';
        return;
      }
      if (/^[+-]?0\d/.test(raw)) {
        const numeric = Number(raw);
        if (Number.isFinite(numeric)) input.value = String(numeric);
      }
    };

    document.addEventListener('focusin', handleNumericFocus, true);
    document.addEventListener('focusout', handleNumericBlur, true);
    return () => {
      document.removeEventListener('focusin', handleNumericFocus, true);
      document.removeEventListener('focusout', handleNumericBlur, true);
    };
  }, []);

  useEffect(() => {
    const client = getSupabase();
    let cancelled = false;
    const startupTimeout = window.setTimeout(() => { if (!cancelled) setChecking(false); }, 6000);

    client.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      window.clearTimeout(startupTimeout);
      setSession(data.session); setChecking(false);
    }).catch(() => {
      if (cancelled) return;
      window.clearTimeout(startupTimeout); setChecking(false);
    });

    const { data: listener } = client.auth.onAuthStateChange((_event, next) => setSession(next));
    return () => { cancelled = true; window.clearTimeout(startupTimeout); listener.subscription.unsubscribe(); };
  }, [hasKey]);

  if (checking) return <LoadingCrest />;
  if (!hasKey) return <ConnectionScreen onConnected={() => setHasKey(true)} />;
  if (!session) return <LoginScreen onLogin={() => setIntroTrigger((value) => value + 1)} />;

  return (
    <>
      <SplitCrestIntro trigger={introTrigger} />
      <DataProvider>
        <V23Router
          session={session}
          onLogout={async () => {
            await getSupabase().auth.signOut();
            setSession(null);
          }}
        />
      </DataProvider>
    </>
  );
}
